import {
  OrderStage,
  ORDER_STAGE_BY_NUMBER,
  FINISHED_STAGES,
  BLOCKED_FINISH_BUTTON_STAGES,
  ORDER_CANCELLED_STAGES,
  ORDER_REORDER_STAGES,
  TIMER_STOPPED_STAGES,
  PAYMENT_TO_BALANCE_STAGES,
} from '../model/stages';

/**
 * Pure, presentation-agnostic stage helpers. Icons are returned as STRING keys
 * (a stable icon slug) so the UI maps them to lucide-react components — the
 * entity layer stays free of React/MUI. Labels are derived from the stage slug
 * so the UI can translate via `t('orders:stage.<id>')`, falling back to the
 * Russian source label. Ported from cpanel `model/helpers.tsx` +
 * `assets/data/order_stages.ts`.
 */

/** Russian source label for a stage (i18n fallback). */
export function stageLabel(stage: number): string {
  return ORDER_STAGE_BY_NUMBER.get(stage)?.name ?? '';
}

/** Stage slug used to build the i18n key `orders:stage.<id>`. */
export function stageKey(stage: number): string {
  return ORDER_STAGE_BY_NUMBER.get(stage)?.id ?? `stage_${stage}`;
}

/** Brand-tinted hex for the stage status badge. */
export function stageColor(stage: number): string {
  return ORDER_STAGE_BY_NUMBER.get(stage)?.color ?? '#536088';
}

/** Whether the elapsed-time timer should be frozen for a stage. */
export function isTimerStopped(stage: number): boolean {
  return (TIMER_STOPPED_STAGES as readonly number[]).includes(stage);
}

/* ── Row-action gating (stage-based, NOT role-based) ── */

export function canCancelOrder(stage: number): boolean {
  return (ORDER_CANCELLED_STAGES as readonly number[]).includes(stage);
}

export function canFinishStage(stage: number): boolean {
  return (
    !(FINISHED_STAGES as readonly number[]).includes(stage) &&
    !(BLOCKED_FINISH_BUTTON_STAGES as readonly number[]).includes(stage)
  );
}

export function canReorderCourier(
  stage: number,
  opts: {
    vendorOrderId?: string | null | undefined;
    serviceId?: number | null | undefined;
  } = {},
): boolean {
  return (
    (ORDER_REORDER_STAGES as readonly number[]).includes(stage) &&
    opts.vendorOrderId !== 'xolodilnik' &&
    opts.serviceId !== 2
  );
}

/* ── Status-badge tone (soft-fill pill, Figma) ── */

/**
 * Semantic tone for a stage's status pill — the Figma orders table renders each
 * status as a soft-filled pill (`bg-*-100 / text-*-500`). This maps a stage
 * number onto one of the design-system status tones so the UI stays free of
 * per-stage color literals. In-flight legs (5–14, incl. "delivered — awaiting
 * confirmation") read as brand `active` (orange); only the terminal `_finish`
 * stages read `success` (green); cancelled/failed read `danger` (red);
 * scheduling/estimating/returning-in-progress read `pending`; else `neutral`.
 */
export type StageTone = 'active' | 'success' | 'danger' | 'pending' | 'neutral';

/** Terminal successfully-completed stages: order/return finished (green). */
const SUCCESS_STAGES = new Set([15, 20]);
/** Cancelled / failed terminal stages (red). */
const DANGER_STAGES = new Set([21, 22, 23, 24, 25, 27, 28]);

export function stageTone(stage: number): StageTone {
  if (SUCCESS_STAGES.has(stage)) return 'success';
  if (DANGER_STAGES.has(stage)) return 'danger';
  // In-flight legs (searching → delivered/awaiting-confirmation) are brand
  // `active` (orange) — matches the timer-group label the badge shows.
  if (stage >= 5 && stage <= 14) return 'active';
  // Returning legs in progress, and new/estimating/approval/scheduled/returned/
  // not-estimated read as `pending` (amber).
  if ((stage >= 16 && stage <= 19) || (stage >= 1 && stage <= 4) || stage === 26) {
    return 'pending';
  }
  return 'neutral';
}

/* ── Timer-group badge (icon slug + color), shared across grouped stages ── */

export interface StageGroupBadge {
  /** Icon slug → mapped to a lucide icon in the UI. */
  icon: string;
  color: string;
}

/**
 * One icon + color per timer-status group, so stages that share a tooltip label
 * also share their badge. Group ranges MUST stay in sync with
 * `timerTooltipKey`. Ported from cpanel `getStageGroupBadge`.
 */
export function stageGroupBadge(stage: number): StageGroupBadge | null {
  if (stage >= 5 && stage <= 6) return { icon: 'search', color: '#FB923C' };
  if (stage === 7) return { icon: 'assignment-ind', color: 'var(--primary)' };
  if (stage === 8) return { icon: 'store', color: '#0EA5E9' };
  if (stage >= 9 && stage <= 10) return { icon: 'near-me', color: '#0284C7' };
  if (stage >= 11 && stage <= 14) return { icon: 'check-circle', color: '#422AFB' };
  if (stage === 16) return { icon: 'assignment-return', color: '#EB5757' };
  if (stage >= 17 && stage <= 19) return { icon: 'reply', color: '#DB5151' };
  return null;
}

/* ── Detail-page action visibility (shared by header + action bar) ── */

/** The order fields the action-visibility gate reads. */
export interface OrderActionVisibilityInput {
  stage: number;
  vendorOrderId?: string | null | undefined;
  serviceId?: number | null | undefined;
  hasCourier: boolean;
  vendorIsBusiness?: boolean | null | undefined;
  vendorIsCorporate?: boolean | null | undefined;
}

/** Which detail-page actions are shown for an order. */
export interface OrderActionVisibility {
  /** Blind re-order of a fresh courier (cancelled/not-found stages). */
  showReorder: boolean;
  showFinish: boolean;
  showReturn: boolean;
  showCancel: boolean;
  showPaymentToBalance: boolean;
}

/**
 * Single source of truth for which single-order actions are visible, shared by
 * `OrderHeader` and `OrderDetailActions` so the two never drift. Gates are
 * cpanel-parity: stage-based, with the two courier-touching actions
 * (`reorder`/`return`) additionally behind the `couriers.manage_order`
 * permission the page resolves and passes in (a feature can't import
 * access-control). `paymentToBalance` is corporate-business + DELIVERED/RETURNED.
 */
export function computeOrderActionVisibility(
  order: OrderActionVisibilityInput,
  opts: { canManipulateCourier: boolean },
): OrderActionVisibility {
  const { stage, hasCourier } = order;
  return {
    showReorder:
      canReorderCourier(stage, {
        vendorOrderId: order.vendorOrderId,
        serviceId: order.serviceId,
      }) && opts.canManipulateCourier,
    showFinish: canFinishStage(stage),
    showReturn: hasCourier && opts.canManipulateCourier,
    showCancel: canCancelOrder(stage),
    showPaymentToBalance:
      order.vendorIsBusiness === true &&
      order.vendorIsCorporate === true &&
      (PAYMENT_TO_BALANCE_STAGES as readonly number[]).includes(stage),
  };
}

/** i18n key for the timer tooltip label of a stage (`orders:timer.<key>`). */
export function timerTooltipKey(stage: number): string | null {
  if (stage >= 5 && stage <= 6) return 'searching';
  if (stage === 7) return 'heading_to_sender';
  if (stage === 8) return 'waiting_order';
  if (stage >= 9 && stage <= 10) return 'heading_to_recipient';
  if (stage >= 11 && stage <= 14) return 'waiting_recipient';
  if (stage === 16) return 'returning';
  if (stage >= 17 && stage <= 19) return 'in_return';
  return null;
}

/** Russian source labels for timer tooltips (i18n fallback). */
export const TIMER_TOOLTIP_LABELS: Record<string, string> = {
  searching: 'Идет Поиск',
  heading_to_sender: 'Едет к отправителю',
  waiting_order: 'Ждет заказ',
  heading_to_recipient: 'Едет к получателю',
  waiting_recipient: 'Ждет получателя',
  returning: 'В пути для возврата',
  in_return: 'В состоянии возврата',
};

export { OrderStage };
